import { useFetcher, useLoaderData } from '@remix-run/react'
import { useEffect, useRef, useState } from 'react'
import ActionButtons from '../primitives/ActionButton'
import { Loading, NumberField, Toast } from '~/design-system/primitives'
import { useTrackEvents } from '~/hooks/useTrackEvents'
import { Add, CheckIndeterminateSmall, Delete } from '~/icons'
import { cn } from '~/lib/cn'
import type { cartLoader } from '~/loaders/cart'
import type { Product } from '~/type/Cart'

export type ProductQtyChangerProps = {
  quantity: number
  itemId: string
  className?: string
  product: Product
}

export default function ProductQtyChanger({ quantity, itemId, className, product }: ProductQtyChangerProps) {
  let ref = useRef<HTMLFormElement>(null)
  const [showToast, setShowToast] = useState(false)

  const fetcher = useFetcher()
  const { trackAddToCart, trackRemoveFromCart } = useTrackEvents()
  const { items } = useLoaderData<typeof cartLoader>()

  useEffect(() => {
    if (fetcher.formData) {
      const quantityValue = Number(fetcher.formData.get('quantity'))
      if (quantityValue === 0) {
        setShowToast(true)
      }
    }
  }, [fetcher.formData])

  const handleIncrementDecrement = (action: 'increment' | 'decrement') => {
    const totalQuantity = items
      .filter((item) => item.id === itemId)
      .reduce((total, item) => total + item.quantity, 0)

    const newQuantity = action === 'increment' ? totalQuantity + 1 : totalQuantity - 1

    if (action === 'decrement') trackRemoveFromCart([product], '')
    if (action === 'increment') trackAddToCart([product], '')

    fetcher.submit(
      { quantity: String(newQuantity), itemId, actionType: 'changeQuantity' },
      { method: 'post', action: '/checkout/cart' },
    )
  }

  const handleManualChange = (event: React.KeyboardEvent<Element>) => {
    const inputElement = event.target as HTMLInputElement
    const newQuantity = Number(inputElement.value)

    if (!isNaN(newQuantity) && newQuantity > 0 && newQuantity <= 9999) {
      fetcher.submit(
        { quantity: String(newQuantity), itemId, actionType: 'changeQuantity' },
        { method: 'post', action: '/checkout/cart' },
      )
    } else {
      inputElement.value = String(quantity)
    }
  }

  return (
    <>
      <fetcher.Form
        action="/checkout/cart"
        className={cn(className, 'relative')}
        data-comp="ProductQtyChanger"
        method="post"
        ref={ref}
      >
        <input name="actionType" type="hidden" value="changeQuantity" />
        <input name="itemId" type="hidden" value={itemId} />
        <NumberField
          aria-label="Quantidade"
          className="flex-1 w-full text-center justify-center"
          decrementButton={
            <ActionButtons
              aria-label="Diminuir quantidade"
              icon={quantity > 1 ? <CheckIndeterminateSmall /> : <Delete />}
              slot="decrement"
              type="button"
              onPress={() => handleIncrementDecrement('decrement')}
            />
          }
          defaultValue={quantity}
          errorProps={{ className: 'hidden' }}
          formatOptions={{
            signDisplay: 'never',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }}
          groupProps={{
            className: 'flex flex-row gap-2 justify-center items-center',
          }}
          incrementButton={
            <ActionButtons
              aria-label="Aumentar quantidade"
              className="border bg-gray-100 hover:opacity-30"
              icon={<Add className="fill-white" />}
              slot="increment"
              type="button"
              onPress={() => handleIncrementDecrement('increment')}
            />
          }
          maxValue={9999}
          name="quantity"
          value={quantity}
          isRequired
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleManualChange(e)
            }
          }}
        />
        {fetcher.state !== 'idle' && (
          <div className="text-gray-500 absolute top-0 left-0 right-0 bottom-0 flex justify-center items-center bg-white/90">
            <Loading />
          </div>
        )}
      </fetcher.Form>
      {showToast && (
        <Toast
          altText={'Produto excluído com sucesso'}
          content={'Produto excluído com sucesso'}
          status="success"
        />
      )}
    </>
  )
}
